import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, KeyRound, Loader2, Link2, Palette, Pencil, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, Field, Loading, PageHeader, Surface } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { CreatorCatalogSettings } from "@/components/creator/CreatorCatalogSettings";

const EMPTY = { display_name: "", legal_name: "", email: "", phone: "", slug: "",
                commission_percent: 20, active: true };

function CreatorForm({ creator, onClose, onSaved }) {
  const [form, setForm] = useState(creator ? { ...EMPTY, ...creator } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, commission_percent: Number(form.commission_percent) };
      if (creator) await api.put(`/creators/${creator.id}`, body);
      else await api.post("/creators", body);
      toast.success(creator ? "Criadora atualizada." : "Criadora cadastrada.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal open onOpenChange={onClose} title={creator ? "Editar criadora" : "Nova criadora"}
                     description={creator?.display_name || "Parceria por comissão sobre vendas"}
                     testid="creator-form-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nome de exibição *">
          <Input value={form.display_name} onChange={set("display_name")} required
                 data-testid="creator-name-input" />
        </Field>
        <Field label="Nome completo / razão social" hint="Opcional">
          <Input value={form.legal_name || ""} onChange={set("legal_name")}
                 data-testid="creator-legal-input" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="E-mail *">
            <Input type="email" value={form.email} onChange={set("email")} required
                   data-testid="creator-email-input" />
          </Field>
          <Field label="Telefone" hint="Opcional">
            <Input value={form.phone || ""} onChange={set("phone")} data-testid="creator-phone-input" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Slug do catálogo *" hint="A URL fica /catalogodalu, por exemplo">
            <Input value={form.slug} onChange={set("slug")} required placeholder="catalogodalu"
                   data-testid="creator-slug-input" />
          </Field>
          <Field label="Comissão padrão (%) *" hint="Pode ser 0 se for intencional">
            <Input type="number" min="0" max="100" step="0.5" value={form.commission_percent}
                   onChange={set("commission_percent")} required
                   data-testid="creator-commission-input" />
          </Field>
        </div>
        <Field label="Situação">
          <NativeSelect value={String(form.active)}
                        onChange={(v) => setForm((f) => ({ ...f, active: v === "true" }))}
                        options={[{ value: "true", label: "Ativa" },
                                  { value: "false", label: "Inativa" }]}
                        data-testid="creator-active-select" />
        </Field>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full"
                  data-testid="creator-cancel-btn">Cancelar</Button>
          <Button type="submit" disabled={busy} className="flex-1 rounded-full"
                  data-testid="creator-save-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
}

function CreatorDetail({ creator, products, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [selected, setSelected] = useState(creator.products.map((p) => p.id));

  const load = async () => {
    const { data: res } = await api.get(`/creators/${creator.id}/commissions`);
    setData(res);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [creator.id]);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pending = (data?.commissions || []).filter((c) => c.status === "PENDENTE");

  return (
    <ResponsiveModal open onOpenChange={onClose} title={creator.display_name}
                     description={`/${creator.slug} · ${creator.commission_percent}% de comissão`}
                     testid="creator-detail-modal">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[["Vendas", brl(data?.sales_total || 0)], ["A pagar", brl(data?.pending_total || 0)],
            ["Pago", brl(data?.paid_total || 0)], ["Em análise", data?.review_count ?? 0]]
            .map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-secondary/40 p-3">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="font-num text-sm">{value}</p>
            </div>
          ))}
        </div>

        <Field label="Produtos vinculados" hint="Só o admin vincula. O produto segue as mesmas regras comerciais do catálogo.">
          <div className="max-h-44 space-y-1 overflow-y-auto rounded-xl border border-white/10 bg-secondary/30 p-3"
               data-testid="creator-products-list">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={selected.includes(p.id)}
                       onChange={(e) => setSelected((s) => e.target.checked
                         ? [...s, p.id] : s.filter((i) => i !== p.id))}
                       data-testid={`creator-product-${p.id}`} />
                <span className="truncate">{p.name}</span>
              </label>
            ))}
          </div>
        </Field>
        <Button variant="outline" disabled={busy} className="w-full gap-2 rounded-full"
                data-testid="creator-link-products-btn"
                onClick={() => run(() => api.put(`/creators/${creator.id}/products`,
                                                 { product_ids: selected }),
                                   "Vínculos atualizados.")}>
          <Link2 className="h-3.5 w-3.5" /> Salvar vínculos
        </Button>


        <Field label="Acesso da criadora" hint="Cria ou redefine a senha do painel dela">
          <div className="flex gap-2">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="Senha (mín. 6)" data-testid="creator-password-input" />
            <Button variant="outline" disabled={busy || password.length < 6}
                    className="shrink-0 gap-2 rounded-full" data-testid="creator-access-btn"
                    onClick={() => run(() => api.post(`/creators/${creator.id}/access`,
                                                      { password }), "Acesso configurado.")}>
              <KeyRound className="h-3.5 w-3.5" /> Salvar
            </Button>
          </div>
        </Field>

        <div className="rounded-xl border border-white/10 bg-secondary/30 p-4" data-testid="creator-commissions">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Comissões</p>
          <ul className="mt-2 space-y-1.5">
            {(data?.commissions || []).length === 0 && (
              <li className="text-xs text-muted-foreground">Nenhuma comissão gerada ainda.</li>
            )}
            {(data?.commissions || []).map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate">
                  {c.order_number} · {c.product_name} · {c.quantity}x ·{" "}
                  <span className="text-muted-foreground">{c.rate}% de {brl(c.base)}</span>
                </span>
                <span className="shrink-0 font-num">
                  {brl(c.amount)}{" "}
                  <span className="text-muted-foreground">{c.status.toLowerCase()}</span>
                </span>
              </li>
            ))}
          </ul>
          {pending.length > 0 && (
            <Button disabled={busy} className="mt-3 w-full rounded-full"
                    data-testid="creator-payout-btn"
                    onClick={() => run(() => api.post(`/creators/${creator.id}/payouts`,
                                                      { commission_ids: pending.map((c) => c.id),
                                                        method: "PIX" }),
                                       "Repasse registrado.")}>
              Registrar repasse de {brl(pending.reduce((a, c) => a + c.amount, 0))}
            </Button>
          )}
          {(data?.payouts || []).length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-white/10 pt-2" data-testid="creator-payouts">
              {data.payouts.map((p) => (
                <li key={p.id} className="flex justify-between gap-2 text-[11px] text-muted-foreground">
                  <span>{dataBR(p.paid_at)} · {p.method} · {p.commission_ids.length} comissão(ões)</span>
                  <span className="font-num">{brl(p.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default function Creators() {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [opened, setOpened] = useState(null);
  const [styling, setStyling] = useState(null);
  const { startCreatorImpersonation } = useAuth();
  const navigate = useNavigate();

  // Visualização administrativa: a sessão admin é guardada e restaurada ao sair.
  const enterCreatorArea = async (creator) => {
    try {
      await startCreatorImpersonation(creator.id);
      toast.success(`Visualização administrativa de ${creator.display_name}.`);
      navigate("/criadora");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = async () => {
    setError("");
    try {
      const [{ data: res }, { data: prods }] = await Promise.all([
        api.get("/creators"), api.get("/products")]);
      setData(res);
      setProducts((prods.products || prods || []).map((p) => ({ id: p.id, name: p.name })));
      setOpened((prev) => (prev ? res.creators.find((c) => c.id === prev.id) || null : null));
    } catch (e) {
      setError(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => (data?.creators || []).reduce(
    (acc, c) => ({ pending: acc.pending + c.pending_total, sales: acc.sales + c.sales_total }),
    { pending: 0, sales: 0 }), [data]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Carregando criadoras..." />;

  return (
    <div className="space-y-6" data-testid="creators-page">
      <PageHeader
        title="Parceiras Criadoras"
        subtitle={data.creators.length === 0
          ? "Cadastre uma criadora para gerar o catálogo exclusivo dela."
          : `${brl(totals.sales)} em vendas · ${brl(totals.pending)} de comissão a pagar`}
        action={
          <Button onClick={() => setCreating(true)} data-testid="new-creator-btn"
                  className="h-11 gap-2 rounded-full px-5 font-semibold">
            <Plus className="h-4 w-4" /> Nova criadora
          </Button>
        }
      />

      {data.creators.length === 0 ? (
        <EmptyState icon={Sparkles} title="Nenhuma criadora cadastrada"
                    description="A criadora fornece o STL; a TR3ND fabrica e vende. Ela acompanha vendas e comissões no painel dela." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0" data-testid="creators-list">
          {data.creators.map((c) => (
            <Surface key={c.id} className="space-y-3 p-5" data-testid={`creator-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-medium">{c.display_name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    /{c.slug} · {c.commission_percent}% · {c.active ? "ativa" : "inativa"}
                    {!c.has_access && " · sem acesso"}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(c)}
                        className="h-8 shrink-0 rounded-full" data-testid={`creator-edit-${c.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {[["Produtos", c.products.length], ["A pagar", brl(c.pending_total)],
                  ["Pago", brl(c.paid_total)]].map(([l, v]) => (
                  <div key={l}>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</p>
                    <p className="font-num">{v}</p>
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={() => setOpened(c)}
                      className="w-full rounded-full text-xs" data-testid={`creator-open-${c.id}`}>
                Produtos, comissões e acesso
              </Button>
              <div className="grid gap-2 sm:grid-cols-2">
                <Button variant="soft" onClick={() => enterCreatorArea(c)}
                        disabled={!c.has_access}
                        className="w-full gap-1.5 rounded-full text-xs"
                        data-testid={`creator-portal-${c.id}`}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  {c.has_access ? "Ver portal" : "Sem acesso criado"}
                </Button>
                <Button variant="soft" onClick={() => setStyling(c)}
                        className="w-full gap-1.5 rounded-full text-xs"
                        data-testid={`creator-style-${c.id}`}>
                  <Palette className="h-3.5 w-3.5" /> Vitrine
                </Button>
              </div>
            </Surface>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <CreatorForm creator={editing} onClose={() => { setCreating(false); setEditing(null); }}
                     onSaved={load} />
      )}
      {opened && (
        <CreatorDetail creator={opened} products={products} onClose={() => setOpened(null)}
                       onChanged={load} />
      )}
      {styling && (
        <ResponsiveModal open onOpenChange={() => setStyling(null)}
                         title={`Vitrine de ${styling.display_name}`}
                         description={`Cores e banners do catálogo /${styling.slug}`}
                         testid="creator-style-modal">
          <CreatorCatalogSettings data={{ creator: styling }}
                                  endpoint={`/creators/${styling.id}/catalog-settings`} />
        </ResponsiveModal>
      )}
    </div>
  );
}
